// Types and small helpers for Final Decision > Actions > Automatic Contracting.

export interface OfferProfile {
  id: string;
  label: string;
  is_default: boolean;
  financing_type?: string;
  missing: string[];
}

export interface OfferBuyer {
  id: string;
  name: string;
  self: boolean;
  signing: { available: boolean; reason: string | null };
  profiles: OfferProfile[];
}

export interface OfferTemplate {
  id: string;
  name: string;
  form_number: string | null;
  version: string | null;
}

export interface OfferAddendum {
  id: string;
  name: string;
  addendum_tag?: string | null;
}

export interface OfferSetup {
  buyers: OfferBuyer[];
  templates: OfferTemplate[];
  addenda: OfferAddendum[];
  commission_ready: boolean;
  credits: number;
}

export interface PreviewInput {
  list_sort_id: string;
  property_id: string;
  buyer_id: string;
  buyer_profile_id: string;
  template_id: string;
  addendum_ids: string[];
  price: string;
  effective_date: string;
  sign?: boolean;
}

export interface CreateOffersInput {
  list_sort_id: string;
  buyer_id: string;
  buyer_profile_id: string;
  template_id: string;
  addendum_ids: string[];
  effective_date: string;
  sign?: boolean;
  items: { property_id: string; price: string }[];
}

// A generated, stored contract package.
export interface StoredOffer {
  id: string;
  property_id: string;
  price: number;
  effective_date: string;
  created_at: string;
  buyer_name: string;
  created_by_name: string;
  documents: string[];
  acting_as_agent: boolean;
  signed: boolean;
}

export interface CreateOffersResult {
  success: boolean;
  results: StoredOffer[];
  errors: Record<string, string[]> | string[];
  shared_with: string | null;
  credits?: number;
}

export const groupOffersByProperty = (offers: StoredOffer[]): Record<string, StoredOffer[]> =>
  offers.reduce<Record<string, StoredOffer[]>>((groups, offer) => {
    (groups[offer.property_id] ||= []).push(offer);
    return groups;
  }, {});

export const formatOfferPrice = (price: number): string =>
  `$${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export const offerPdfUrl = (id: string): string => `/contract_offers/${id}/pdf`;

// One .zip with every one of these contracts as its own PDF, titled with the property address.
export const offersZipUrl = (ids: string[]): string =>
  `/contract_offers/export?${ids.map((id) => `ids[]=${encodeURIComponent(id)}`).join('&')}`;

// The Max Bid from the table, as text for the price box (no "$" or commas so it can be edited).
export const priceInputFromMaxBid = (maxBid: number | null | undefined): string =>
  typeof maxBid === 'number' && maxBid > 0 ? maxBid.toFixed(2) : '';

export const isValidPrice = (value: string): boolean => {
  const number = Number(value.replace(/[$,\s]/g, ''));
  return value.trim() !== '' && Number.isFinite(number) && number > 0;
};

export const todayIso = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

// The profile to pre-select for a buyer: their default, otherwise the first one.
export const defaultProfileId = (buyer: OfferBuyer | undefined): string =>
  buyer ? (buyer.profiles.find((p) => p.is_default) ?? buyer.profiles[0])?.id ?? '' : '';

// "cash" -> "Cash", "texas_veterans" -> "Texas veterans"
export const financingLabel = (type?: string): string => {
  const words = (type || 'cash').replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};
